/**
 * DelegatePrizeManagementPage — /delegate/prize-management
 *
 * The third Event Operations surface, alongside Draws and Live Scoring. It
 * consolidates prize configuration and the money behind it so an admin never
 * has to walk My Rodeos → event → edit just to see or change a payout.
 *
 * Rodeos come from `useMyRodeos` (GET /jackpot-ops/my/rodeos), which is already
 * org-scoped server-side — a delegate is only offered rodeos they can actually
 * open. The per-rodeo endpoints re-check that scope anyway; this page shows
 * contestant payment records, so the list being scoped is a convenience, not the
 * security boundary.
 */

import { useMemo, useState } from 'react';
import { Trophy, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { PrizeRodeoCard } from '@/components/delegate/prize-management/PrizeRodeoCard';
import { useRodeosPrizeOverview } from '@/api/extensions/usePrizeOverview';
import { useMyRodeos } from '@/hooks/useMyRodeos';
import {
  type ScheduleTab,
  isPastSchedule,
  sortScheduleByTab,
} from '@/utils/scheduleTabs';
import { createScheduleListState } from '@/utils/scheduleListState';

// Which rodeos are open, and which tab you were on, survive a refresh and a
// navigate-away — same session-scoped model Manage Draws and Live Scoring use,
// via the shared helper. Namespaced to this page, so expanding a rodeo here
// doesn't expand it on Draws.
const viewState = createScheduleListState('delegate-prize-management');

export default function DelegatePrizeManagementPage() {
  const [search, setSearch] = useState('');
  const { data, isLoading, isError, error } = useMyRodeos({ page_size: 100 });

  // Upcoming vs past. Both the split and the sort come from @/utils/scheduleTabs
  // — the SAME helpers Manage Draws and Live Scoring use — so all three lists
  // agree on what "past" means and show rodeos in the same order. In particular
  // past-ness is judged against the rodeo's END date in the rodeo's OWN
  // timezone, so a multi-day rodeo running tonight stays Upcoming until
  // midnight where it is being held, not midnight where the admin is sitting.
  const [tab, setTabState] = useState<ScheduleTab>(() => viewState.loadTab());

  const setTab = (next: ScheduleTab) => {
    setTabState(next);
    viewState.saveTab(next);
  };

  // Expansion is owned here (not per-card) so it can be persisted. The set spans
  // both tabs — a rodeo you left open on Past is still open when you switch back
  // to Past, without Upcoming having to know anything about it.
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() =>
    viewState.loadExpandedIds(),
  );

  const toggleRodeo = (rodeoId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(rodeoId)) next.delete(rodeoId);
      else next.add(rodeoId);
      viewState.saveExpandedIds(next);
      return next;
    });
  };

  const allRodeos = useMemo(() => data?.data ?? [], [data]);

  const counts = useMemo(() => {
    let upcoming = 0;
    let past = 0;
    for (const r of allRodeos) {
      if (isPastSchedule(r)) past += 1;
      else upcoming += 1;
    }
    return { upcoming, past };
  }, [allRodeos]);

  // The tab's rodeos, before the search box narrows them. This is what drives
  // the fetch — search is a client-side filter over data we already hold, so
  // typing must not refire the request on every keystroke.
  const tabRodeos = useMemo(
    () =>
      sortScheduleByTab(
        allRodeos.filter((r) => (tab === 'past') === isPastSchedule(r)),
        // Upcoming → soonest first; Past → most recent first.
        tab,
      ),
    [allRodeos, tab],
  );

  const rodeos = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return tabRodeos;
    return tabRodeos.filter((r) =>
      String(r.name ?? '').toLowerCase().includes(term),
    );
  }, [tabRodeos, search]);

  // ONE batched request for the visible rodeos' allocation figures, so every
  // card shows its locked / potential / not-included totals COLLAPSED — the
  // numbers this page exists to show shouldn't need a click. The backend
  // resolves them all in a fixed number of queries, so this costs fewer
  // round-trips than the request-per-card-on-expand it replaced. Scoping the ids
  // to the current tab means opening Upcoming doesn't price every past rodeo.
  const rodeoIds = useMemo(() => tabRodeos.map((r) => r.id), [tabRodeos]);
  const { data: overviews, isLoading: overviewsLoading } =
    useRodeosPrizeOverview(rodeoIds);

  const overviewByRodeo = useMemo(
    () => new Map((overviews ?? []).map((o) => [o.rodeo.id, o])),
    [overviews],
  );

  return (
    <div className="space-y-4 p-4 md:p-6">
      <header className="space-y-1">
        <h1 className="flex items-center gap-2 text-2xl font-bold">
          <Trophy className="h-6 w-6 text-amber-500" aria-hidden="true" />
          Prize Management
        </h1>
        <p className="text-sm text-muted-foreground">
          Prize setup and payout allocation for every rodeo you manage. Expand a
          rodeo to see how each event&apos;s pool splits between money that&apos;s
          locked in, money that&apos;s still potential, and entries that
          aren&apos;t included — and to edit the prize configuration in place.
        </p>
      </header>

      {/* Upcoming / Past — same control, ordering and past-ness rule as the
          Draws and Live Scoring tabs. Upcoming is the default so only the work
          in front of the admin shows. */}
      <div className="flex items-center gap-1 border-b border-border">
        {(
          [
            ['upcoming', 'Upcoming', counts.upcoming],
            ['past', 'Past', counts.past],
          ] as [ScheduleTab, string, number][]
        ).map(([id, label, count]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            aria-current={tab === id ? 'page' : undefined}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === id
                ? 'border-blue-500 text-blue-500'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-400'
            }`}
          >
            {label} Rodeos ({count})
          </button>
        ))}
      </div>

      <div className="relative max-w-sm">
        <Search
          className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search rodeos…"
          aria-label="Search rodeos"
          className="pl-8"
        />
      </div>

      {isLoading && (
        <p className="py-8 text-center text-sm text-muted-foreground">
          Loading your rodeos…
        </p>
      )}

      {isError && (
        <p className="py-8 text-center text-sm text-red-600 dark:text-red-400">
          {error instanceof Error ? error.message : 'Could not load your rodeos.'}
        </p>
      )}

      {data && rodeos.length === 0 && (
        <p className="py-8 text-center text-sm text-muted-foreground">
          {search.trim()
            ? `No ${tab} rodeos match “${search.trim()}”.`
            : tab === 'upcoming'
              ? 'You don’t have any upcoming rodeos.'
              : 'You don’t have any past rodeos.'}
        </p>
      )}

      <div className="space-y-2">
        {rodeos.map((rodeo) => (
          <PrizeRodeoCard
            key={rodeo.id}
            rodeo={{
              id: rodeo.id,
              name: String(rodeo.name ?? 'Unnamed rodeo'),
              start_date: (rodeo.start_date as string | null) ?? null,
              is_published: rodeo.is_published,
            }}
            overview={overviewByRodeo.get(rodeo.id)}
            isLoading={overviewsLoading}
            expanded={expandedIds.has(rodeo.id)}
            onToggle={() => toggleRodeo(rodeo.id)}
          />
        ))}
      </div>
    </div>
  );
}
